import { useState, useRef, useLayoutEffect } from "react";
import type { TeamLogMessage } from "../types";
import { generateSummary } from "../lib/summaryGenerator";
import { roleFromAuthor } from "./Avatars";
import { LogLinkText } from "./LogLinkText";

function formatTs(iso: string): { date: string; time: string } {
  try {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
    };
  } catch {
    return { date: "", time: "" };
  }
}

function AuthorChip({ author }: { author: string }) {
  const role = roleFromAuthor(author);
  if (author === "relay") {
    return (
      <span className="presence-chip relay-chip entry-author-chip">
        <img src="/relay-mark.svg" style={{ width: 12, height: 12, display: "block" }} alt="" />
        {author}
      </span>
    );
  }
  return (
    <span className="presence-chip entry-author-chip">
      <span className={`presence-chip-dot ${role}`} />
      {author}
    </span>
  );
}

interface CollapsibleEntryProps {
  message: TeamLogMessage;
  defaultOpen?: boolean;
  flashId?: string | null;
  /** Active tag filters, so a chip can show it is already applied. */
  activeFilters?: string[];
  onToggleFilter?: (tag: string) => void;
  onStartReply?: (seq: number) => void;
  onScrollToSequence?: (seq: number) => void;
}

// The head is a <button>, so controls inside it are role="button" spans
// (nested <button> is invalid HTML). Click must not toggle the entry.
function inlineButtonProps(onActivate: () => void) {
  return {
    role: "button" as const,
    tabIndex: 0,
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      onActivate();
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        onActivate();
      }
    },
  };
}

export function CollapsibleEntry({
  message,
  defaultOpen = false,
  flashId,
  activeFilters = [],
  onToggleFilter,
  onStartReply,
  onScrollToSequence,
}: CollapsibleEntryProps) {
  const [open, setOpen] = useState(defaultOpen);
  const innerRef = useRef<HTMLDivElement>(null);
  const [maxH, setMaxH] = useState<number>(0);

  useLayoutEffect(() => {
    setMaxH(open && innerRef.current ? innerRef.current.scrollHeight : 0);
  }, [open]);

  const { date, time } = formatTs(message.timestamp);
  const summary = generateSummary(message);
  const author = typeof message.author === "string" ? message.author : String(message.author);
  const isFlashed = flashId === message.id;

  return (
    <div
      className={`entry collapsible${open ? " open" : ""}`}
      data-message-id={message.id}
      style={{
        outline: isFlashed ? "2px solid var(--er-accent)" : "none",
        outlineOffset: isFlashed ? 2 : 0,
        transition: "outline-color 200ms ease-out",
      }}
    >
      <button
        type="button"
        className="entry-head"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <AuthorChip author={author} />

        <div className="entry-tags">
          {message.tags?.map((t, i) => {
            if (!onToggleFilter) return <span className="tag" key={i}>{t}</span>;
            const isActive = activeFilters.includes(t);
            return (
              <span
                className="tag"
                key={i}
                {...inlineButtonProps(() => onToggleFilter(t))}
                title={isActive ? `Remove filter: ${t}` : `Filter stream by: ${t}`}
                style={{ cursor: "pointer" }}
              >
                {t}
              </span>
            );
          })}
        </div>

        <div className="entry-ts inline">
          {date}<span className="time">{time}</span>
        </div>

        {message.sequence != null && (
          onStartReply ? (
            <span
              className="entry-id"
              {...inlineButtonProps(() => onStartReply(message.sequence!))}
              title={`Reply to #${message.sequence}`}
              style={{ cursor: "pointer" }}
            >
              #{message.sequence}
            </span>
          ) : (
            <span className="entry-id">#{message.sequence}</span>
          )
        )}
        <span className="entry-chevron" aria-hidden="true">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M3 2 L7 5 L3 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      <p className="entry-summary">{summary}</p>

      <div className="entry-detail" style={{ maxHeight: maxH }}>
        <div className="entry-detail-inner" ref={innerRef}>
          {message.replyToSequence != null && (
            <div className="entry-row">
              {onScrollToSequence ? (
                <button
                  type="button"
                  className="tag reply"
                  onClick={() => onScrollToSequence(message.replyToSequence!)}
                  title={`Go to #${message.replyToSequence}`}
                  style={{ cursor: "pointer" }}
                >
                  Re: #{message.replyToSequence}
                </button>
              ) : (
                <span className="tag reply">Re: #{message.replyToSequence}</span>
              )}
            </div>
          )}
          <div className="entry-msg" style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.55 }}>
            <LogLinkText text={message.content} embedImages={message.tags?.includes("visual-check-result") ?? false} />
          </div>
        </div>
      </div>
    </div>
  );
}
